import { Box, IconButton, Menu, MenuButton, MenuItem, MenuList, useDisclosure } from '@chakra-ui/react';
import { faCog } from '@fortawesome/pro-regular-svg-icons';
import { ReactElement } from 'react';
import { useDispatch } from 'react-redux';
import { Icon } from 'shared-components';
import { exemptApplicantRequirement } from '@/store/apply/actions';
import { openToast } from '@/store/ui/actions';
import { RequirementExemptModal } from './modals/RequirementExemptModal';

type RequirementCogMenuProps = (
  | {
      // Default mode: dispatches exemptApplicantRequirement with the given requirementId.
      requirementId: string;
      applicantId: string;
      uni: string;
      offerId: string;
      onExempt?: undefined;
      hasTasks?: boolean;
    }
  | {
      // External callback mode: caller handles exemption (e.g. bulk-exempting a section).
      requirementId?: undefined;
      applicantId: string;
      uni: string;
      offerId: string;
      onExempt: (noteText: string) => void;
      hasTasks?: boolean;
    }
) & {
  /** When provided, renders "Select studies to assess" menu item (used for academic requirements). */
  onSelectStudies?: () => void;
};

const RequirementCogMenu = ({
  requirementId,
  applicantId,
  uni,
  offerId,
  onExempt,
  hasTasks,
  onSelectStudies,
}: RequirementCogMenuProps): ReactElement => {
  const dispatch = useDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleExemptClick = (): void => {
    if (hasTasks === false) {
      dispatch(openToast({ title: 'No tasks to exempt for this requirement', status: 'warning' }));
      return;
    }
    onOpen();
  };

  const handleExemptConfirm = (noteText: string): void => {
    if (onExempt) {
      onExempt(noteText);
    } else {
      dispatch(
        exemptApplicantRequirement({
          applicantId,
          uni,
          offerId,
          requirementId,
          noteText: noteText || undefined,
        }),
      );
    }
    onClose();
  };

  return (
    <>
      <RequirementExemptModal isOpen={isOpen} onClose={onClose} onConfirm={handleExemptConfirm} />
      <Box my={-1}>
        <Menu placement="bottom-end">
          <MenuButton
            as={IconButton}
            icon={<Icon icon={faCog} color="blue.500" />}
            size="sm"
            variant="ghost"
            colorScheme="blue"
            aria-label="Requirement actions"
          />
          <MenuList>
            {onSelectStudies && <MenuItem onClick={onSelectStudies}>Select studies to assess</MenuItem>}
            <MenuItem onClick={handleExemptClick}>Exempt requirement</MenuItem>
          </MenuList>
        </Menu>
      </Box>
    </>
  );
};

export default RequirementCogMenu;
